import { useEffect, useState, useRef } from "react";
import { subscribePumpPortalTokenTrades } from "@/lib/pumpPortalRealtime";
import { getSolUsd } from "@/lib/solUsd";

// Duplicated from usePumpPortalTrades for simplicity since it's not exported
function parseNum(v: unknown): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number.parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function parseMcUsd(msg: Record<string, unknown>, solUsd: number): number | null {
  const usdKeys = [
    "marketCapUsd", "usdMarketCap", "marketCapUSD", "mcUsd", 
    "market_cap_usd", "mc_usd", "usd_market_cap",
    "bondingCurveMarketCapUsd"
  ];
  for (const key of usdKeys) {
    const n = parseNum(msg[key]);
    if (n > 0) return n;
  }
  const mcSol = parseNum(
    msg.marketCapSol ?? msg.market_cap_sol ?? msg.vSolInBondingCurve ?? msg.bondingCurveMarketCapSol,
  );
  if (mcSol > 0 && solUsd > 0) return mcSol * solUsd;
  return null;
}

export function useLivePrices(mints: string[]) {
  const [prices, setPrices] = useState<Record<string, number>>({});
  const solUsdRef = useRef(150);
  const activeSubs = useRef<Set<string>>(new Set());

  useEffect(() => {
    void getSolUsd().then(v => { solUsdRef.current = v; });
    const tick = setInterval(() => {
      void getSolUsd().then(v => { solUsdRef.current = v; });
    }, 60000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    const unsubs: (() => void)[] = [];
    const currentMints = new Set(mints.filter(m => m && m.trim().length > 0));

    // Subscribe to new mints
    for (const mint of currentMints) {
      if (!activeSubs.current.has(mint)) {
        activeSubs.current.add(mint);
        
        const unsub = subscribePumpPortalTokenTrades(mint, (msg) => {
          const raw = msg.data ?? msg.payload ?? msg.trade ?? msg.event ?? msg;
          const items = Array.isArray(raw) ? raw : [raw];
          
          let latestMc: number | null = null;
          for (const item of items) {
            if (item && typeof item === "object") {
              const mc = parseMcUsd(item as Record<string, unknown>, solUsdRef.current);
              if (mc) latestMc = mc;
            }
          }

          if (latestMc) {
            setPrices(prev => {
              if (prev[mint] === latestMc) return prev;
              return { ...prev, [mint]: latestMc! };
            });
          }
        });
        
        unsubs.push(() => {
          activeSubs.current.delete(mint);
          unsub();
        });
      }
    }

    return () => {
      unsubs.forEach(fn => fn());
    };
  }, [mints.join(",")]);

  return prices;
}
